import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft2, Eye, EyeSlash } from 'iconsax-react';
import { type PropsWithChildren, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { z } from 'zod';
import ProgressButton from '@/components/common/progress-button';
import { FieldMessage } from '@/components/fields/field-message';
import { PhoneField } from '@/components/fields/phone-field';
import useRequestOtp from '@/hooks/auth/use-request-otp';
import ROUTES from '@/routes';
import { addToast } from '@/stores/toast';
import cn from '@/utils/merge-class-names';

interface SignInFormProps {
  isLoading?: boolean;
  disabled?: boolean;
  className?: string;
  navigateBack?: boolean;
  onSubmit: (data: SignInFormData, withOtp: boolean) => void;
}

export const SignInForm = ({
  className,
  onSubmit,
  isLoading,
  children,
  disabled = false,
  navigateBack = false,
}: PropsWithChildren<SignInFormProps>) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    register,
    control,
    handleSubmit,
    getValues,
    resetField,
    formState: { errors },
  } = useForm<SignInFormData>({
    resolver: zodResolver(signInFormScheme),
    defaultValues: {
      countryCode: '86',
      phoneNumber: '',
      password: '',
    },
  });
  const [withOtp, setWithOtp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { request, isPending: isRequestingOtp, countdown } = useRequestOtp('sign-in');

  const handleOtpRequest = () => {
    const values = getValues();

    if (!values.phoneNumber) {
      return addToast({ type: 'error', message: t('auth.phone-required') });
    }
    return request({
      Phone: values.phoneNumber,
      TelCode: values.countryCode,
    });
  };

  const toggleWithOtp = (setTo?: boolean) => {
    setWithOtp((s) => setTo ?? !s);
    resetField('password');
  };

  return (
    <form
      className={cn('flex size-full flex-1 flex-col', className)}
      data-testid='sign-in-form'
      onSubmit={handleSubmit((d) => onSubmit(d, withOtp))}>
      <div className='form-control mb-8 mode-embedded:mb-4'>
        <div
          role='tablist'
          className='flex desktop:justify-center gap-10'>
          <button
            type='button'
            role='tab'
            disabled={disabled || isLoading}
            className={cn('border-b-2 border-b-transparent py-2 text-primary-content/50', {
              'border-b-neutral-50 text-neutral-50': !withOtp,
            })}
            onClick={() => toggleWithOtp(false)}>
            {t('auth.sign-in-by-password')}
          </button>
          <button
            type='button'
            role='tab'
            disabled={disabled || isLoading}
            className={cn('border-b-2 border-b-transparent py-2 text-primary-content/50', {
              'border-b-neutral-50 text-neutral-50': withOtp,
            })}
            onClick={() => toggleWithOtp(true)}>
            {t('auth.sign-in-by-otp')}
          </button>
        </div>
      </div>

      <div className='form-control w-full'>
        <PhoneField
          disabled={disabled || isLoading}
          names={['countryCode', 'phoneNumber']}
          placeholder={t('auth.phone-number')}
          control={control}
        />

        <FieldMessage
          name='phoneNumber'
          errors={errors}
        />
      </div>

      <div className='form-control relative mt-4 w-full'>
        <div className='input px-0 join input-bordered border ring-0 pe-0 bg-neutral-0 has-[:disabled]:bg-neutral-200'>
          <input
            type={withOtp || showPassword ? 'text' : 'password'}
            inputMode={withOtp ? 'numeric' : 'text'}
            placeholder={withOtp ? t('auth.one-time-password') : t('auth.password')}
            autoComplete='current-password'
            className='join-item w-full flex-1 px-4'
            disabled={disabled || isLoading}
            {...register('password')}
          />

          {withOtp ? (
            countdown <= 0 || countdown >= 60 ? (
              <button
                type='button'
                disabled={isRequestingOtp || disabled || isLoading}
                onClick={handleOtpRequest}
                className='btn btn-ghost border-0 text-primary'>
                {t('auth.request-otp')}
              </button>
            ) : (
              <button
                type='button'
                disabled
                className='btn btn-ghost border-0 '>
                {t('auth.retry-after-seconds', { seconds: countdown })}
              </button>
            )
          ) : (
            <button
              type='button'
              className='join-item btn btn-ghost'
              disabled={disabled || isLoading}
              onClick={() => setShowPassword(!showPassword)}>
              {showPassword ? <Eye /> : <EyeSlash />}
            </button>
          )}
        </div>

        <FieldMessage
          name='password'
          errors={errors}
        />
      </div>

      <div className='flex justify-end mt-4'>
        <Link
          className='link link-neutral text-sm'
          to={ROUTES.AUTH.FORGOT_PASSWORD.$path()}>
          {t('auth.forgot-password')}
        </Link>
      </div>

      <div className='flex-1'>{children}</div>

      <div className='form-control gap-4 mt-8 flex flex-row mode-embedded:mt-4'>
        {navigateBack && (
          <button
            type='button'
            className='absolute desktop:static left-4 top-safe-offset-4 btn btn-square desktop:btn-circle btn-ghost desktop:border text-primary-content'
            onClick={() => navigate(-1)}>
            <ArrowLeft2 className='aspect-square size-6' />
          </button>
        )}

        <ProgressButton
          as='submit'
          isLoading={isLoading}
          disabled={disabled}
          className='btn btn-revert-primary flex-1'
          label='auth.sign-in'
        />
      </div>
    </form>
  );
};

const signInFormScheme = z.object({
  countryCode: z.string().min(1),
  phoneNumber: z.string().min(1, 'errors.string.length|count=1').regex(/^\d+$/),
  password: z.string().min(1, 'errors.string.length|count=1'),
});

export type SignInFormData = z.infer<typeof signInFormScheme>;
